<script setup lang="ts">
import type { Props } from '@/shared/ui/AppSort/model/types/appSort';

const props = withDefaults(defineProps<Props>(), {
  defaultLabel: '',
});

const route = useRoute();

const isMobile: boolean = useIsMobile();
const isShowSortList: Ref<boolean> = ref(false);
const contentRef = ref<HTMLElement | null>(null);
const isOverflowRight: Ref<boolean> = ref(false);
const isOverflowLeft: Ref<boolean> = ref(false);

const selectedSort: ComputedRef<string> = computed(() => {
  const label =
    props.options.find((option) => option.value === props.modelValue)?.label ??
    props.defaultLabel;
  if (!label) return '';
  return label.charAt(0).toLowerCase() + label.slice(1);
});

const sortButtonLabel: ComputedRef<string> = computed(() =>
  selectedSort.value ? `Сортировка: по ${selectedSort.value}` : 'Сортировка',
);

const updateOverflow = (): void => {
  const el = contentRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  if (rect.right > window.innerWidth) isOverflowRight.value = true;
  if (rect.left < 0) isOverflowLeft.value = true;
};

useElementResize(contentRef, updateOverflow);

const toggleList = (): void => {
  isShowSortList.value = !isShowSortList.value;
};

const hideList = (): void => {
  isShowSortList.value = false;
};

watch(isShowSortList, (value) => {
  if (value) return;
  isOverflowRight.value = false;
  isOverflowLeft.value = false;
});

watch(
  () => route.fullPath,
  () => {
    isShowSortList.value = false;
  },
);
</script>

<template>
  <div v-click-outside="hideList" :class="['app-sort', { _mobile: isMobile }]">
    <button
      class="app-sort__button"
      type="button"
      :aria-label="sortButtonLabel"
      @click.prevent="toggleList"
    >
      <div class="app-sort__label">
        <template v-if="selectedSort">По {{ selectedSort }}</template>
        <AppIcon name="ArrowDownSmall" />
      </div>
    </button>
    <div
      v-if="isShowSortList"
      ref="contentRef"
      :class="[
        'app-sort__content',
        { '_overflow-right': isOverflowRight },
        { '_overflow-left': isOverflowLeft },
      ]"
    >
      <span class="app-sort__text">Сортировать по:</span>
      <ul class="app-sort__list">
        <li v-for="option in options" :key="option.id" class="app-sort__list-item">
          <NuxtLink
            class="app-sort__list-link"
            :to="option.link"
            :exact-active-class="option.value === modelValue ? '_active' : ''"
          >
            {{ option.label }}
          </NuxtLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="less">
.app-sort {
  display: flex;
  position: relative;

  &__button {
    font-size: @size-x3;
    line-height: @size-x3;
    font-weight: 600;
    min-width: 99px;
  }

  &__label {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    color: @color-manatee;
    white-space: nowrap;

    .app-icon {
      width: @size-x6;
      height: @size-x6;
      min-width: @size-x6;
      margin-left: 10px;
    }
  }

  &__content {
    .flex-column(@size-x3);
    .box-shadow();

    position: absolute;
    left: -130px;
    z-index: 10;
    width: 324px;
    background: #fff;
    border-radius: @main-size;
    top: calc(100% + @size-x3);
    padding: @size-x6 @size-x8;

    @media @1280 {
      width: 250px;
    }

    &._overflow-right {
      left: auto;
      right: 0;
    }

    &._overflow-left {
      left: 0;
      right: auto;
    }
  }

  &__list {
    .flex-column(@size-x3);
  }

  &__list-item {
    font-weight: 400;
    font-size: @size-x4;
    line-height: 22px;
    color: @color-elephant;
  }

  &__list-link {
    position: relative;
    padding-left: 30px;

    &::before {
      content: '';
      position: absolute;
      width: @size-x5;
      height: @size-x5;
      background: #fff;
      border: 1px solid @color-silver-chalice;
      border-radius: 50%;
      left: 0;
      top: 2px;
    }

    &._active::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 7px;
      width: 10px;
      height: 10px;
      background-color: @color-persimmon;
      border-radius: 100%;
    }
  }

  &__text {
    display: block;
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 700;
  }

  &._mobile &__list-item {
    font-size: 14px;
    line-height: 18px;
  }

  &._mobile &__button {
    text-align: left;
  }

  &._mobile &__label {
    justify-content: left;
  }

  &._mobile &__content {
    left: 0;
  }
}
</style>
